import React, { Fragment, useCallback } from 'react';
import styled from 'styled-components';
import { RelativeTime } from '@99designs/frontend-components/components/RelativeTime';
import { formatMessage } from '@99designs/frontend-components/i18n';
import TrackScrollToPageElement from '../../../../shared/components/Analytics/TrackScrollToPageElement';
import Box from '../../../../shared/components/Box';
import CompletedContestCard from '../../../../shared/components/Card/CompletedContestCard';
import { CompletedContestCardPlaceholderCard } from '../../../../shared/components/Card/PlaceholderCard';
import Heading from '../../../../shared/components/Heading';
import Scrollable from '../../../../shared/components/Scrollable';
import { getTitleForPackageType } from '../../../../shared/lib/getTitleForPackageType';
import { categoryLaunchPath } from '../../../../shared/lib/platformUrls';
import { media, spaces } from '../../../../shared/lib/styles';
import Currency from '../../I18n/Currency';
import { ContestCategoryType, RelatedContestType } from '../ContestOverviewData';

export interface Props {
    relatedContests: RelatedContestType;
    contestCategory: ContestCategoryType;
}

const ItemWrapper = styled.div`
    width: 350px;

    @media ${media('small')} {
        width: 320px;
    }
`;

function RelatedContests({ contestCategory, relatedContests }: Props) {
    const scrollableRef = useCallback((element) => {
        if (element) {
            element.offCenter(350);
        }
    }, []);

    relatedContests = relatedContests
        .slice()
        .sort((a, b) => (a.completedAt <= b.completedAt ? 1 : -1));

    return (
        <>
            <TrackScrollToPageElement
                pageName="Contest Overview"
                pageElement="Related Contests Section"
            >
                <div className="row">
                    <div className="column small-12 medium-12 normal-4 large-4 normal-offset-6 large-offset-6">
                        <Heading type="h2" variations={['h1', 'no-margin']}>
                            {__('We think contests are a super fun way to get design.')}
                        </Heading>
                        <Box
                            paddingTop={['space10', 'space14', 'space14', 'space14']}
                            paddingBottom={['space10', 'space10', 'space10', 'space10']}
                        >
                            <Heading type="h3" variations={['h5', 'no-margin']}>
                                {__('Recently completed contests:')}
                            </Heading>
                        </Box>
                    </div>
                </div>
            </TrackScrollToPageElement>

            <Scrollable
                ref={scrollableRef}
                variations={['full-page-width', 'padded']}
                style={{ paddingBottom: spaces.space8 }}
            >
                {relatedContests.map((contest, index) => (
                    <Fragment key={contest.id}>
                        <ItemWrapper data-testid="related-contest" id={contest.id}>
                            <CompletedContestCard
                                completedAt={<RelativeTime date={contest.completedAt} />}
                                contestPath={contest.relativeUrl}
                                title={contest.title}
                                description={contest.subTitle}
                                categoryKey={contest.category.key}
                                packageTitle={getTitleForPackageType(contest.package.type)}
                                design={{
                                    url: contest.winningDesignImageUrl,
                                    alt: formatMessage(
                                        __('"{contestTitle}" winning {contestCategory}'),
                                        {
                                            contestTitle: contest.title,
                                            contestCategory: contestCategory.title,
                                        }
                                    ),
                                }}
                                stats={[
                                    { label: 'entries', value: contest.entryCount },
                                    { label: 'designers', value: contest.designerCount },
                                ]}
                            />
                        </ItemWrapper>

                        {index + 1 === Math.ceil(relatedContests.length / 2) && (
                            <div style={{ width: 350, display: 'flex' }}>
                                <Box paddingTop="space10" paddingBottom="space10">
                                    <CompletedContestCardPlaceholderCard
                                        ctaUrl={categoryLaunchPath(contestCategory.key)}
                                        ctaLabel={__('Get your own design')}
                                        title={__('We’ve saved a spot for your design contest :)')}
                                    />
                                </Box>
                            </div>
                        )}
                    </Fragment>
                ))}
            </Scrollable>
        </>
    );
}

export default RelatedContests;
